import { Link } from 'react-router-dom';
import { Sparkles, ArrowLeft } from 'lucide-react';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-indigo-700 rounded-xl flex items-center justify-center">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-serif font-bold text-slate-900">StatsXY</span>
          </Link>
          <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-indigo-600">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sm:p-12">
          <h1 className="text-3xl sm:text-4xl font-serif font-bold text-slate-900 mb-2">
            Privacy Policy
          </h1>
          <p className="text-lg text-indigo-600 mb-8">
            StatsXY – AI-Powered Statistics & Research Analytics Platform
          </p>

          <div className="prose prose-slate max-w-none">
            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">1. INTRODUCTION</h2>
            <p className="text-slate-700 mb-4">
              StatsXY ("StatsXY", "we", "us", or "our") is committed to protecting your privacy.
            </p>
            <p className="text-slate-700 mb-4">
              This Privacy Policy explains how we collect, use, store, and protect personal and research data when you access or use the StatsXY platform ("Platform").
            </p>
            <p className="text-slate-700 mb-4 font-medium">
              By using StatsXY, you agree to the practices described in this Privacy Policy.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">2. INFORMATION WE COLLECT</h2>
            
            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">2.1 Personal Information</h3>
            <p className="text-slate-700 mb-4">We may collect the following personal data when you register or use the Platform:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Full name</li>
              <li>Email address</li>
              <li>University or institution</li>
              <li>Level of study and field of study</li>
              <li>Account login credentials (passwords are securely hashed)</li>
            </ul>

            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">2.2 Payment Information</h3>
            <p className="text-slate-700 mb-4">Payments are processed securely via PayPal.</p>
            <p className="text-slate-700 mb-4 font-medium">StatsXY does not store your card details.</p>
            <p className="text-slate-700 mb-4">
              Payment data is handled directly by PayPal in accordance with their privacy and security standards.
            </p>

            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">2.3 Research Data & Content</h3>
            <p className="text-slate-700 mb-4">We collect research-related data that you voluntarily upload, including:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Quantitative datasets (e.g. CSV, Excel)</li>
              <li>Qualitative data (e.g. interview transcripts, text files)</li>
              <li>Research titles, aims, objectives, and project metadata</li>
            </ul>
            <p className="text-indigo-700 font-medium mb-4 bg-indigo-50 p-3 rounded-lg">
              You retain full ownership of your research data.
            </p>

            <h3 className="text-lg font-medium text-slate-800 mt-4 mb-2">2.4 Technical & Usage Information</h3>
            <p className="text-slate-700 mb-4">We may automatically collect:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>IP address</li>
              <li>Browser type and device information</li>
              <li>Login timestamps</li>
              <li>Feature usage patterns</li>
            </ul>
            <p className="text-slate-700 mb-4">This information is used for:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Security</li>
              <li>Performance monitoring</li>
              <li>Platform improvement</li>
            </ul>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">3. HOW WE USE YOUR INFORMATION</h2>
            <p className="text-slate-700 mb-4">StatsXY uses collected data to:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Provide and operate the Platform</li>
              <li>Perform statistical and qualitative analyses</li>
              <li>Generate charts, tables, and visual outputs</li>
              <li>Manage subscriptions and access control</li>
              <li>Improve platform performance and features</li>
              <li>Respond to support requests</li>
            </ul>
            <p className="text-green-700 font-medium mb-4 bg-green-50 p-3 rounded-lg">
              We do not sell or rent your personal or research data.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">4. AI PROCESSING & DATA HANDLING</h2>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Research data is processed by AI algorithms only for the purpose of providing analysis</li>
              <li>Data is not used to train public AI models</li>
              <li>AI outputs are generated in-session or stored only within your secure user account</li>
              <li>We do not access or review your research content unless required for support or legal reasons</li>
            </ul>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">5. DATA STORAGE & SECURITY</h2>
            <p className="text-slate-700 mb-4">
              StatsXY implements reasonable technical and organizational measures to protect your data, including:
            </p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Secure authentication mechanisms</li>
              <li>Encrypted data transmission (HTTPS)</li>
              <li>Access controls and user data isolation</li>
            </ul>
            <p className="text-slate-700 mb-4">
              Despite best efforts, no system is completely secure. You acknowledge and accept this inherent risk.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">6. DATA RETENTION</h2>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Account data is retained while your account is active</li>
              <li>Research data remains accessible to you while your account is active</li>
              <li>Upon account deletion, data may be permanently removed after a reasonable period unless retention is required by law</li>
            </ul>
            <p className="text-slate-700 mb-4 font-medium">
              Users are responsible for exporting any data they wish to retain before account termination.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">7. DATA SHARING & DISCLOSURE</h2>
            <p className="text-slate-700 mb-4">StatsXY does not share your data with third parties except:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Payment processing via PayPal</li>
              <li>Hosting, security, or analytics services strictly necessary to operate the Platform</li>
              <li>Legal obligations, court orders, or regulatory requirements</li>
            </ul>
            <p className="text-slate-700 mb-4">
              Third-party service providers are required to maintain appropriate confidentiality and security standards.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">8. USER RIGHTS</h2>
            <p className="text-slate-700 mb-4">Depending on your jurisdiction, you may have the right to:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Withdraw consent for data processing</li>
            </ul>
            <p className="text-slate-700 mb-4">Requests can be made by contacting StatsXY support.</p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">9. INTERNATIONAL DATA TRANSFERS</h2>
            <p className="text-slate-700 mb-4">
              StatsXY may store or process data on servers located in different countries.
            </p>
            <p className="text-slate-700 mb-4">
              By using the Platform, you consent to such transfers in accordance with this Privacy Policy.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">10. COOKIES & TRACKING</h2>
            <p className="text-slate-700 mb-4">StatsXY may use essential cookies to:</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Maintain login sessions</li>
              <li>Improve user experience</li>
            </ul>
            <p className="text-slate-700 mb-4 font-medium">
              We do not use invasive tracking or advertising cookies.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">11. CHILDREN'S PRIVACY</h2>
            <p className="text-slate-700 mb-4">
              StatsXY is not intended for users under the age of 18.
            </p>
            <p className="text-slate-700 mb-4">
              We do not knowingly collect personal data from minors.
            </p>

            <hr className="my-6" />

            <h2 className="text-xl font-semibold text-slate-900 mt-8 mb-4">12. CHANGES TO THIS PRIVACY POLICY</h2>
            <p className="text-slate-700 mb-4">We may update this Privacy Policy periodically.</p>
            <ul className="list-disc pl-6 mb-4 text-slate-700 space-y-2">
              <li>Updates will be posted on the Platform</li>
              <li>Continued use constitutes acceptance of the revised policy</li>
            </ul>

            <hr className="my-6" />

            <div className="bg-indigo-50 p-6 rounded-xl mt-8">
              <h2 className="text-xl font-semibold text-indigo-900 mb-3">FINAL NOTE</h2>
              <p className="text-indigo-800 mb-2">
                StatsXY respects the sensitive nature of academic and research data.
              </p>
              <p className="text-indigo-800 font-medium">
                Our goal is to provide powerful analytical assistance while maintaining privacy, security, and user control.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Links */}
        <div className="mt-8 text-center text-sm text-slate-500">
          <Link to="/terms" className="hover:text-indigo-600 mx-3">Terms & Conditions</Link>
          <span>|</span>
          <Link to="/contact" className="hover:text-indigo-600 mx-3">Contact Us</Link>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPage;
